import React from 'react';
import {connect} from 'react-redux';
import PropTypes from 'prop-types';
import {Button, Carousel, Icon, Input, Modal, notification} from 'antd';
import {isModalOpened, modalData} from '../../../redux/selectors/nav';
import {makeAction} from '../../../redux/actions/makeAction';
import {
  CLOSE_MODAL,
  OPEN_MODAL,
  REQUEST_PROFILE_EDIT_CODE,
  REQUEST_EMAIL_EDIT_CODE,
  USER_PROFILE_CHANGE,
  VALIDATE_CODE,
} from '../../../redux/actions/actionTypes';
import FormError from '../../FormError';
import {authErrors, profileErrors} from '../../../redux/selectors/error';
import {API_CODE_LENGTH, PROFILE_EDIT_EMAIL_MODAL_ID, VerificationCodeType} from '../../../services/constants';
import VerificationCodeForm from '../common/VerificationCodeForm';
import {withTranslation} from 'react-i18next';
import TranslatedText from '../../TranslatedText';
import {ReactComponent as ArrowRight} from '../../../assets/img/arrowRight.svg';
import {ReactComponent as CloseIcon} from '../../../assets/img/close.svg';
import {userEmailSelector} from '../../../redux/selectors/user';
import {ReactComponent as CloseNotificationIcon} from '../../../assets/img/CloseIconNotification.svg';

const defaultState = {
  email: '',
  emailRepeat: '',
  code_invalid: false,
  code: ' '.repeat(API_CODE_LENGTH),
  codeNewEmail: ' '.repeat(API_CODE_LENGTH),
};

class ChangeEmailModal extends React.PureComponent {
  constructor(props) {
    super(props);
    this.state = {...defaultState};
    this.submit = this.submit.bind(this);
    this.onCancel = this.onCancel.bind(this);
    this.onCodeChange = this.onCodeChange.bind(this);
    this.onCodeNewEmailChange = this.onCodeNewEmailChange.bind(this);
    this.onEmailChange = this.onEmailChange.bind(this);
    this.backToEmail = this.backToEmail.bind(this);
    this.codeSubmit = this.codeSubmit.bind(this);
    this.carousel = React.createRef();
    this.resendCode = this.resendCode.bind(this);
    this.resendNewCode = this.resendNewCode.bind(this);
  }

  submit() {
    this.props.changeUserProfile({
      field: 'email',
      code: this.state.code,
      codeNewEmail: this.state.codeNewEmail,
      value: this.state.email,
      valueRepeat: this.state.emailRepeat,
      callback: () => {
        this.props.closeModal();
        this.cleanup();
        notification.warn({
          message: null,
          className: 'profile-changed-tooltip-card',
          closeIcon: <CloseNotificationIcon/>,
          description: this.props.t('modals.profile.emailChanged'),
          icon: <Icon type="info-circle" />,
        });
      },
    });
  }

  codeSubmit(code) {
    this.setState({code_invalid: false});
    this.props.validateCode({
      code,
      type: VerificationCodeType.UPDATE_FRAGILE_DATA,
      callback: () => {
        this.setState({code});
        this.carousel.current.next();
      },
      onError: () => {
        this.setState({code_invalid: true});
      },
    });
  }

  onCodeChange(code) {
    this.setState({code});
  }

  onCodeNewEmailChange(codeNewEmail) {
    this.setState({codeNewEmail});
  }

  onEmailChange(event) {
    event.preventDefault();
    this.props.requestEmailEditCode({
      email: this.state.email,
      emailRepeat: this.state.emailRepeat,
      callback: () => {
        this.carousel.current.next();
      },
    });
  }

  backToEmail() {
    this.carousel.current.prev();
  }

  onCancel() {
    this.props.closeModal();
    this.cleanup();
  }

  cleanup() {
    this.setState(defaultState);
    this.carousel.current.goTo(0);
  }

  resendCode() {
    this.props.requestProfileEditCode({
      email: this.props.email,
    });
  }

  resendNewCode() {
    this.props.requestEmailEditCode({
      email: this.state.email,
      emailRepeat: this.state.emailRepeat,
    });
  }

  render() {
    return (
      <Modal
        title={<TranslatedText transKey="modals.profile.editEmail"/>}
        className="profile-modal change-email-profile-modal"
        width={440}
        visible={this.props.opened}
        closeIcon={<CloseIcon/>}
        onCancel={this.onCancel}
        footer={null}
      >
        <Carousel ref={this.carousel} dots={false} infinite={false}>
          <div>
            <VerificationCodeForm onChange={this.onCodeChange} code={this.state.code} onSubmit={this.codeSubmit}
                                  isInvalid={this.state.code_invalid} onResend={this.resendCode}>
              <Button type="primary" block htmlType="submit">
                <TranslatedText transKey="modals.verification.next"/>
              </Button>
            </VerificationCodeForm>
          </div>
          <div>
            <FormError errors={this.props.authErrors} fieldName="non_field_errors"/>
            <form onSubmit={this.onEmailChange}>
              <div className="modal-form-group">
                <FormError.Tooltip errors={this.props.authErrors} fieldName="email">
                  <Input placeholder={this.props.t('modals.profile.emailNew')} name="email" value={this.state.email}
                         onChange={e => this.setState({email: e.target.value})} required/>
                </FormError.Tooltip>
              </div>
              <div className="modal-form-group">
                <FormError.Tooltip errors={this.props.authErrors} fieldName="emailRepeat">
                  <Input placeholder={this.props.t('modals.profile.emailRepeat')} name="emailRepeat" value={this.state.emailRepeat}
                         onChange={e => this.setState({emailRepeat: e.target.value})} required/>
                </FormError.Tooltip>
              </div>
              <Button type="primary" block htmlType="submit">
                <TranslatedText transKey="modals.verification.next"/>
              </Button>
            </form>
          </div>
          <div>
            <FormError errors={this.props.errors} fieldName="non_field_errors"/>
            <VerificationCodeForm onChange={this.onCodeNewEmailChange} code={this.state.codeNewEmail} onSubmit={this.submit}
                                  onResend={this.resendNewCode} isInvalid={this.state.code_invalid}>
              <Button type="primary" block htmlType="submit">
                <TranslatedText transKey="modals.profile.saveChanges"/>
              </Button>
              <div className="submit-subelement">
                <a onClick={this.backToEmail}>
                  <ArrowRight className="action-icon right"/>
                  <TranslatedText transKey="modals.common.stepBack"/>
                </a>
              </div>
            </VerificationCodeForm>
          </div>
        </Carousel>
      </Modal>
    );
  }
}

ChangeEmailModal.propTypes = {
  opened: PropTypes.bool.isRequired,
  data: PropTypes.object,
  errors: PropTypes.object.isRequired,
  authErrors: PropTypes.object.isRequired,
  changeUserProfile: PropTypes.func.isRequired,
  closeModal: PropTypes.func.isRequired,
  validateCode: PropTypes.func.isRequired,
  openModal: PropTypes.func.isRequired,
  t: PropTypes.func.isRequired,
  requestProfileEditCode: PropTypes.func.isRequired,
  requestEmailEditCode: PropTypes.func.isRequired,
  email: PropTypes.string,
};

const mapStateToProps = (state) => ({
  opened: isModalOpened(state)(PROFILE_EDIT_EMAIL_MODAL_ID),
  data: modalData(state),
  errors: profileErrors(state),
  authErrors: authErrors(state),
  email: userEmailSelector(state),
});

const mapDispatchToProps = {
  openModal: makeAction(OPEN_MODAL),
  closeModal: makeAction(CLOSE_MODAL),
  changeUserProfile: makeAction(USER_PROFILE_CHANGE),
  validateCode: makeAction(VALIDATE_CODE),
  requestProfileEditCode: makeAction(REQUEST_PROFILE_EDIT_CODE),
  requestEmailEditCode: makeAction(REQUEST_EMAIL_EDIT_CODE),
};

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(ChangeEmailModal));



// WEBPACK FOOTER //
// /src/components/modals/profile/ChangeEmailModal.jsx